import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';

import * as fromDrug from './drug.reducer';
import * as DrugActions from './drug.actions';
import { APIService } from '../../services/api/api.service';
import { ListQueryService } from '../../components/common/pagination/list-query.service';
import { listInvalidationEffect } from '../../components/common/pagination/list-query-invalidation.effect';
import { DRUG_LIST_QUERY_PREFIX } from './drug.query-keys';

@Injectable()
export class DrugEffects {
  // A drug mutation can change what the list page shows, so drop the keyed
  // list caches (the offset/SWR path the list page renders from). The other
  // consumers (drug selector, pre-auth, customer prescriptions) are unaffected —
  // they read the full set from the NgRx collection.
  invalidateListQueries$ = listInvalidationEffect(
    () => this.actions$,
    () => this.listQuery,
    DRUG_LIST_QUERY_PREFIX,
    [DrugActions.saveDrugSuccess, DrugActions.updateDrugSuccess]
  );

  loadDrugs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DrugActions.loadDrugs),
      switchMap(action =>
        this.apiService
          .getDrugs(action.filters)
          .pipe(
            // getDrugs returns a union: a bare array when no includeCount is
            // sent (these callers), or an envelope otherwise. Normalise to the
            // full array so the reverted full-set behaviour is preserved.
            map(res => (Array.isArray(res) ? res : res.data)),
            map(drugs => DrugActions.loadDrugsSuccess({ drugs })),
            catchError(error => {
              console.error('Error', error);
              this.notification.error('Error', 'There was an error loading drugs.');
              return of(DrugActions.loadDrugsFailure({ error }));
            })
          )
      )
    )
  );

  loadDrugByDin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DrugActions.loadDrugByDin),
      switchMap(action =>
        this.apiService.getDrugByDin(action.din).pipe(
          map(drug => DrugActions.loadDrugByDinSuccess({ drug })),
          catchError(error => {
            const message = error?.message || 'Failed to load drug';
            this.notification.error('Error', message);
            return of(DrugActions.loadDrugByDinFailure({ error: message }));
          })
        )
      )
    )
  );

  saveDrug$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DrugActions.saveDrug),
      switchMap(action =>
        this.apiService.saveDrug(action.drug).pipe(
          tap(() => this.notification.success('Successfully saved.', '')),
          map(drug => DrugActions.saveDrugSuccess({ drug })),
          catchError(error => {
            console.error('Error', error);
            this.notification.error('Error', 'There was an error saving drug.');
            return of(DrugActions.saveDrugFailure({ error }));
          })
        )
      )
    )
  );

  updateDrug$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DrugActions.updateDrug),
      switchMap(action =>
        this.apiService.updateDrug(action.id, action.drug).pipe(
          tap(() => this.notification.success('Successfully updated.', '')),
          map(drug => DrugActions.updateDrugSuccess({ drug })),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              `There was an error updating drug: ${JSON.stringify(error, null, 2)}`
            );
            return of(DrugActions.updateDrugFailure({ error }));
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private apiService: APIService,
    private notification: NzNotificationService,
    private listQuery: ListQueryService
  ) { }
}
